/**
 * Client HTTP condiviso (ky).
 *
 * `prefixUrl: '/api'` → in dev il proxy Vite inoltra a http://127.0.0.1:3000
 * (vedi vite.config.js); in produzione l'API è servita sotto lo stesso origin.
 * `credentials: 'include'` → invia/riceve il cookie di sessione admin.
 */

import ky from 'ky'
import { getClientId } from './clientId.js'
import { getDisplayMode } from './displayMode.js'

export const api = ky.create({
  prefixUrl: '/api',
  credentials: 'include',
  timeout: 8000,
  hooks: {
    // Identità client anonima per la presence (conteggio utenti connessi).
    // Innocua anche sulle richieste admin (che si identificano via jti).
    beforeRequest: [
      (request) => { request.headers.set('X-Client-Id', getClientId()) }
    ]
  }
})

/**
 * Helper namespaced per le route /api/admin. Ogni metodo ritorna direttamente
 * la promise dei dati JSON parsati (errori propagati come HTTPError di ky).
 *
 * Pattern: api.admin.matches.list({day, fieldId, status, category})
 */
export const adminApi = {
  matches: {
    list: (params = {}) => {
      const qs = new URLSearchParams(
        Object.entries(params).filter(
          ([, v]) => v !== undefined && v !== null && v !== ''
        )
      )
      const query = qs.toString()
      return api.get(`admin/matches${query ? `?${query}` : ''}`).json()
    },
    detail: (idOrCode) => api.get(`admin/matches/${idOrCode}`).json(),
    putSets: (idOrCode, sets) =>
      api.put(`admin/matches/${idOrCode}/sets`, { json: { sets } }).json(),
    confirm: (idOrCode) =>
      api.post(`admin/matches/${idOrCode}/confirm`).json(),
    unconfirm: (idOrCode) =>
      api.post(`admin/matches/${idOrCode}/unconfirm`).json(),
    walkover: (idOrCode, winner, note) =>
      api
        .post(`admin/matches/${idOrCode}/walkover`, {
          json: { winner, ...(note ? { note } : {}) }
        })
        .json(),
    // Editing manuale (override): crea/edita/cancella singola partita scheduled.
    create: (payload) =>
      api.post('admin/matches', { json: payload }).json(),
    update: (idOrCode, payload) =>
      api.put(`admin/matches/${idOrCode}`, { json: payload }).json(),
    // Scambio orario tra due partite scheduled (la destinazione è la cella occupata).
    swap: (idOrCode, payload) =>
      api.post(`admin/matches/${idOrCode}/swap`, { json: payload }).json(),
    remove: (idOrCode) =>
      api.delete(`admin/matches/${idOrCode}`).json()
  },
  fields: {
    list: () => api.get('admin/fields').json()
  },
  teams: {
    list: () => api.get('admin/teams').json(),
    // L'API è bulk-upsert per design (incolla lista nomi). Wrapper di comodo
    // per il caso single-team del pannello "+ Nuova squadra".
    create: ({ categoryCode, name }) =>
      api
        .post('admin/teams', { json: { categoryCode, names: [name] } })
        .json(),
    // Variante multi-nome dello stesso endpoint, usata dal "Genera random"
    // (top-up fino a totalExpected). I duplicati vengono saltati lato server.
    createBulk: ({ categoryCode, names }) =>
      api
        .post('admin/teams', { json: { categoryCode, names } })
        .json(),
    update: (id, { name }) =>
      api.put(`admin/teams/${id}`, { json: { name } }).json(),
    // 204 senza body; 409 se la squadra ha partite in calendario.
    remove: (id) =>
      api.delete(`admin/teams/${id}`),
    // Riassegna manualmente il campo (fase calendar). `fieldNumber` è il numero
    // globale del campo; il server lo risolve nella categoria della squadra
    // (400 se di un'altra categoria; 422 FIELD_FULL se il campo è già a 6).
    moveField: (id, fieldNumber) =>
      api
        .patch(`admin/teams/${id}/field`, { json: { fieldNumber } })
        .json()
  },
  tournament: {
    get: () => api.get('admin/tournament').json(),
    // Update parziale: oggi solo `startDate` (formato 'YYYY-MM-DD'); il
    // backend (PUT /admin/tournament) accetta anche `status` ma è gestito
    // da altri flow (draw, generate, finalize).
    update: ({ startDate }) =>
      api.put('admin/tournament', { json: { startDate } }).json(),
    // Transizione esplicita init → setup (workflow utente: "Conferma
    // impostazioni" in /admin/settings). 409 se non in 'init'.
    confirmInit: () =>
      api.post('admin/tournament/confirm-init').json(),
    // Finalizza UNA categoria ('A'|'P'). Quando entrambe sono finalizzate il
    // torneo passa a status 'done'. Richiede tutti i match della categoria in
    // 'confirmed'/'walkover' (422 con dettaglio pendingMatches altrimenti).
    finalize: ({ reason, categoryCode }) =>
      api.post('admin/tournament/finalize-group-stage', { json: { reason, categoryCode } }).json(),
    // Verifica leggera pre-finalizzazione di una categoria: ritorna
    // { categoryCode, alreadyFinalized, pendingCount, ok }.
    finalizePrecheck: (categoryCode) =>
      api.get('admin/tournament/finalize-precheck', { searchParams: { category: categoryCode } }).json(),
    // Reset completo dati torneo (solo superadmin lato server).
    // `confirmation` deve essere il token "RESET" altrimenti il server ritorna 400.
    // Post-reset: tournament_config.status passa a 'init'.
    reset: ({ reason, confirmation }) =>
      api
        .post('admin/tournament/reset', { json: { reason, confirmation } })
        .json()
  },
  standings: {
    get: () => api.get('admin/standings').json(),
    setTiebreak: (groupKey, orderedTeamIds) =>
      api.put('admin/standings/tiebreak', { json: { groupKey, orderedTeamIds } }).json()
  },
  draw: {
    // Esegue il sorteggio campi per la categoria indicata. Parametri di
    // generazione: `fields` (campi), `maxPerField` (capienza), `fill`
    // ('fill' = riempimento | 'balanced' = uguale numero). 409 se già
    // sorteggiata (richiede `reset` prima); 422 se incompatibile (incl.
    // campi_pro + campi_ama > 12).
    run: ({ categoryCode, fields, maxPerField, fill }) =>
      api.post('admin/draw', { json: { categoryCode, fields, maxPerField, fill } }).json(),
    reset: ({ categoryCode }) =>
      api.post('admin/draw/reset', { json: { categoryCode } }).json(),
    // Scambia due squadre tra campi diversi (stessa categoria, fase calendar).
    // 400 se stesso campo o categoria diversa; 409 se non in fase calendar.
    swap: ({ teamIdA, teamIdB }) =>
      api.post('admin/draw/swap', { json: { teamIdA, teamIdB } }).json()
  },
  daySlots: {
    // Ritorna { categories: [{ categoryCode, days: [{ day, slots: [...] }] }] }.
    list: () => api.get('admin/day-slots').json(),
    // Aggiorna gli slot di UNA categoria: `categoryCode` ('A'|'P') + `days` =
    // array di { day, slots: [{ slotIndex, startTime, matchFormat }] }.
    // Backend sostituisce per (categoria, giorno) in transazione.
    // `autoSpace`/`reason` sono opzioni avanzate non usate dalla UI corrente.
    update: (categoryCode, days, { autoSpace = false, reason } = {}) =>
      api.put('admin/day-slots', {
        json: { categoryCode, days, ...(autoSpace ? { autoSpace } : {}), ...(reason ? { reason } : {}) }
      }).json()
  },
  schedule: {
    // Generazione iniziale (o forzata) del calendario. `scope` default 'all',
    // `force=true` cancella i match `scheduled` esistenti e ricrea da zero
    // (richiesto se esistono già match per lo scope).
    generate: (scope = 'all', force = false) =>
      api
        .post('admin/schedule/generate', { json: { scope, force } })
        .json(),
    regenerate: (fromDay, categoryCode) =>
      api
        .post('admin/schedule/regenerate', { json: { fromDay, categoryCode } })
        .json(),
    // Elimina tutto il calendario e riporta il torneo a 'drawn'. `reason`
    // opzionale per audit.
    deleteCalendar: ({ reason } = {}) =>
      api.delete('admin/schedule', { json: reason ? { reason } : {} }).json()
  },
  scheduleTemplate: {
    get: (category) => api.get('admin/schedule-template', { searchParams: { category } }).json(),
    update: (payload) => api.put('admin/schedule-template', { json: payload }).json()
  },
  finalPhase: {
    get: (category) => api.get('admin/final-phase', { searchParams: { category } }).json(),
    save: (payload) => api.put('admin/final-phase', { json: payload }).json(),
    clear: (category) => api.delete('admin/final-phase', { searchParams: { category } }).json(),
    putSets: (id, body) => api.put(`admin/final-phase/matches/${id}/sets`, { json: body }).json(),
    confirm: (id) => api.post(`admin/final-phase/matches/${id}/confirm`).json(),
    unconfirm: (id) => api.post(`admin/final-phase/matches/${id}/unconfirm`).json()
  },
  audit: {
    listByEntity: (entity, limit = 10) =>
      api.get(`admin/audit?entity=${entity}&limit=${limit}`).json()
  },
  // Backup & restore (solo superadmin lato server). `export` ritorna lo snapshot
  // JSON completo; `restore` lo reimporta sostituendo TUTTI i dati correnti.
  backup: {
    export: () => api.get('admin/backup').json(),
    // `includeFinalPhase` è un parametro dell'operazione → querystring, non body.
    restore: (payload, { includeFinalPhase = false } = {}) =>
      api.post('admin/backup/restore', {
        json: payload,
        searchParams: { includeFinalPhase: includeFinalPhase ? 'true' : 'false' },
        timeout: 30000
      }).json()
  },
  locks: {
    match: {
      acquire: (matchCode) =>
        api.post('admin/locks/match', { json: { matchCode } }).json(),
      heartbeat: (matchCode) =>
        api
          .post('admin/locks/match/heartbeat', { json: { matchCode } })
          .json(),
      // 204 No Content: niente body JSON da parsare → ritorna Promise<Response> raw.
      release: (matchCode) =>
        api.delete('admin/locks/match', { json: { matchCode } })
    }
  },
  // Heartbeat presence admin → { app, admin }.
  presence: {
    ping: () => api.post('admin/presence/ping').json()
  },
  // Statistiche accessi (solo lettura, admin+).
  stats: {
    // Serie storica oraria utenti connessi → { current, hours }.
    presenceHourly: () => api.get('admin/stats/presence-hourly').json(),
    // Composizione del parco dispositivi della PWA (iPhone/Android/Altro →
    // browser/PWA, nome browser, versione OS) → { total, byOs }.
    deviceBreakdown: () => api.get('admin/stats/device-breakdown').json(),
    // Numeri di gioco del torneo (partite/punti/set, split girone/finale,
    // per categoria, record) → { totals, byCategory, records }.
    gameSummary: () => api.get('admin/stats/game-summary').json()
  },
  problemLog: {
    list: (params = {}) => api.get(`admin/problem-log${buildQuery(params)}`).json(),
    // Svuotamento totale (solo superadmin) → { deleted }.
    clear: () => api.delete('admin/problem-log').json()
  }
}

/**
 * Helper read-only per le route /api/public con supporto ETag/304.
 * Ritorna sempre { data, etag, notModified }. Su 304 `data` è undefined e
 * l'etag passato viene restituito invariato (il chiamante riusa la cache).
 */
function buildQuery (params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  )
  const s = qs.toString()
  return s ? `?${s}` : ''
}

async function getPublic (path, { etag } = {}) {
  const res = await api.get(path, {
    headers: etag ? { 'if-none-match': etag } : {},
    throwHttpErrors: false
  })
  if (res.status === 304) return { notModified: true, etag, data: undefined }
  if (res.status >= 400) {
    const err = new Error(`public GET ${path} → ${res.status}`)
    err.status = res.status
    throw err
  }
  return { notModified: false, etag: res.headers.get('etag'), data: await res.json() }
}

export const publicApi = {
  tournament: { get: (opts) => getPublic('public/tournament', opts) },
  teams: { list: (params = {}, opts) => getPublic(`public/teams${buildQuery(params)}`, opts) },
  schedule: { get: (params = {}, opts) => getPublic(`public/schedule${buildQuery(params)}`, opts) },
  standings: { get: (params = {}, opts) => getPublic(`public/standings${buildQuery(params)}`, opts) },
  finalPhase: { get: (opts) => getPublic('public/final-phase', opts) },
  match: { detail: (idOrCode, opts) => getPublic(`public/match/${idOrCode}`, opts) },
  // Heartbeat presence pubblico → { online } (pubblici + admin). L'header
  // X-Display-Mode (browser|standalone) alimenta il breakdown dispositivi:
  // è l'unico segnale che distingue PWA installata da scheda browser.
  presence: {
    ping: () =>
      api.post('public/presence/ping', { headers: { 'X-Display-Mode': getDisplayMode() } }).json()
  }
}
